import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Collapse } from '@mui/material';

// Wrapper that animates an event card into a dashboard section after it
// "arrives" there (e.g. after the host submits a hosting offer, after an
// event is canceled and moves to the canceled section, etc.):
//   1. On mount, smooth-scrolls the (still-collapsed) card to the center of
//      the viewport so the user can watch it animate in. Because MUI Collapse
//      keeps the inner content rendered at its natural size (just clipped to
//      height 0), `scrollIntoView` on the inner Box centers the card at the
//      position it will occupy once expanded.
//   2. After a short delay (long enough for the smooth scroll to mostly
//      finish), the Collapse expands the card into place.
//   3. When the expand transition completes, `onAnimationComplete(eventId)`
//      is invoked so the parent can trigger the secondary border-outline
//      animation on the card.
// Cards that didn't "arrive" (`isArriving=false`) mount fully open with no
// animation.
const AnimatedEventCardSlot = ({
    isArriving,
    eventId,
    onAnimationComplete,
    scrollDelayMs = 750,
    enterTimeoutMs = 400,
    exitTimeoutMs = 250,
    children,
}) => {
    const ref = useRef(null);
    const animateOnMountRef = useRef(Boolean(isArriving));
    const [collapseIn, setCollapseIn] = useState(!isArriving);

    useEffect(() => {
        if (!animateOnMountRef.current) return undefined;
        if (ref.current && ref.current.scrollIntoView) {
            try {
                ref.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            } catch (e) {
                ref.current.scrollIntoView();
            }
        }
        const expandTimer = setTimeout(
            () => setCollapseIn(true),
            scrollDelayMs
        );
        return () => clearTimeout(expandTimer);
    }, [scrollDelayMs]);

    return (
        <Collapse
            in={collapseIn}
            timeout={{ enter: enterTimeoutMs, exit: exitTimeoutMs }}
            sx={{ width: '100%' }}
            onEntered={() => {
                if (!animateOnMountRef.current) return;
                if (typeof onAnimationComplete === 'function' && eventId) {
                    onAnimationComplete(eventId);
                }
            }}
        >
            <Box ref={ref} sx={{ width: '100%' }}>
                {children}
            </Box>
        </Collapse>
    );
};

AnimatedEventCardSlot.propTypes = {
    isArriving: PropTypes.bool,
    eventId: PropTypes.string,
    onAnimationComplete: PropTypes.func,
    scrollDelayMs: PropTypes.number,
    enterTimeoutMs: PropTypes.number,
    exitTimeoutMs: PropTypes.number,
    children: PropTypes.node,
};

export default AnimatedEventCardSlot;
